import DatePicker from "@/components/date-picker";
import { DniSearchInput } from "@/components/input-field/DniSearchInput";
import { EmailValidationInput } from "@/components/input-field/EmailValidationInput";
import { InputField } from "@/components/input-field/InputField";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { PersonaDetalle } from "@/pages/persons/schema/person.schema";
import {
  onlyAlphanumericAndSpaces,
  onlyLetters,
  onlyNumbers,
} from "@/utils/reg-expressions";
import { AuxEntity } from "@/services/aux-entities.service";
import { AppColors } from "@/styles/colors";
import {
  Box,
  Card,
  Field,
  Grid,
  GridItem,
  Text,
  useMediaQuery,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
interface Props {
  personData?: PersonaDetalle | null;
  loadingAuxEntities?: boolean;
  hobbies?: AuxEntity[] | null;
  profesiones?: AuxEntity[] | null;
  nivelesEstudio?: AuxEntity[] | null;
  estadosCiviles?: AuxEntity[] | null;
  sexosBiologicos?: AuxEntity[] | null;
  operation?: "CREATE" | "UPDATE";
}

export const PersonDataForm = ({
  personData,
  loadingAuxEntities,
  estadosCiviles,
  hobbies,
  nivelesEstudio,
  sexosBiologicos,
  profesiones,
  operation,
}: Props) => {
  const [isXS, isSM, isMD, isLG] = useMediaQuery([
    "(max-width: 1350px)",
    "(min-width: 1351px) and (max-width: 1414px)",
    "(min-width: 1415px) and (max-width: 1590px)",
    "(min-width: 1591px) and (max-width: 1696px)",
  ]);

  const agroLabelText = useMemo(() => {
    if (isXS) return "Actividad agro.";
    else if (isSM) return "Actividad agropecuaria";
    else if (isMD) return "Participa en actividad agropecuaria";
    else if (isLG) return "Participa en actividad agropecuaria";

    return "Participa en actividad agropecuaria";
  }, [isXS, isSM, isMD, isLG]);

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  useEffect(() => {
    if (personData) {
      setValue("dni", personData?.dni);
      setValue("nombre", personData?.nombre);
      setValue("apellido", personData?.apellido);

      setValue("cuit", personData?.cuit);
      setValue("telefono", personData?.movil);
      setValue("mail", personData?.mail);
      setValue("apodo", personData?.apodo);
      setValue("localidad", personData?.localidad);
      setValue("sexoBiologico", {
        label: personData?.sexo,
        value: personData?.sexo,
      })

      setValue("agro", [personData?.esProductor]);

      if (personData?.hobbies?.length) {
        setValue(
          "hobbies",
          personData?.hobbies?.map((el) => ({
            label: el.nombre,
            value: el.id,
          })),
        );
      }

      if (personData?.nivelEstudio) {
        setValue("nivelEstudio", {
          label: personData?.nivelEstudio?.nombre,
          value: personData?.nivelEstudio?.id,
        });
      }

      if (personData?.estadoCivil) {
        setValue("estadoCivil", {
          label: personData?.estadoCivil?.nombre,
          value: personData?.estadoCivil?.id,
        });
      }

      if (personData?.profesion) {
        setValue("profesion", {
          label: personData?.profesion?.nombre,
          value: personData?.profesion?.id,
        });
      }

      if (personData?.nacimiento)
        setValue("fecha", dayjs(personData?.nacimiento));
    }
  }, [personData]);

  return (
    <Card.Root
      maxW="full"
      marginTop="30px"
      marginBottom="10px"
      borderRadius="8px"
      minH="auto"
    >
      <Card.Body
        paddingX="48px"
        paddingY="30px"
        borderRadius="8px"
        border={`1px solid ${AppColors.WHITE_BORDER_1}`}
      >
        <Grid templateColumns="repeat(4, 1fr)">
          <GridItem paddingX="16px" paddingY="8px">
            <DniSearchInput
              name="dni"
              labelText="DNI"
              control={control}
              placeholder="Ingresar"
              required
              minHeight="36px"
              maxLength={8}
              disabled={!!personData?.dni}
              validate={operation === "CREATE"}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <InputField
              inputName="nombre"
              labelText="Nombre"
              control={control}
              placeholder="Ingresar"
              minHeight="36px"
              required
              allowedPattern={onlyLetters}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <InputField
              inputName="apellido"
              labelText="Apellido"
              control={control}
              placeholder="Ingresar"
              minHeight="36px"
              required
              allowedPattern={onlyLetters}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px" h="28px">
            <Field.Root>
              <Field.Label>
                {" "}
                <Text fontWeight={600} fontSize="16px">
                  Sexo
                </Text>
              </Field.Label>

              <Box width="100%">
                <AutocompleteSelect
                  disabled={loadingAuxEntities}
                  loading={loadingAuxEntities}
                  inputName="sexoBiologico"
                  items={
                    sexosBiologicos?.map((est) => ({
                      label: est.nombre,
                      value: est.id,
                    })) ?? []
                  }
                  control={control}
                />
              </Box>
            </Field.Root>
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <InputField
              inputName="cuit"
              labelText="CUIL de la persona"
              control={control}
              placeholder="Ingresar"
              minHeight="36px"
              maxLength={11}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <InputField
              inputName="telefono"
              labelText="N° de teléfono"
              control={control}
              placeholder="Ingresar"
              minHeight="36px"
              allowedPattern={onlyNumbers}
              maxLength={13}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <EmailValidationInput
              name="mail"
              labelText="Mail"
              control={control}
              placeholder="Ingresar"
              minHeight="36px"
              validate
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <DatePicker
              control={control}
              labelText="Fecha de nacimiento"
              name="fecha"
              errors={errors?.fecha}
              placeholder="Ingresar fecha"
              maxDate={dayjs()}
              disablePortal={false}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <InputField
              inputName="apodo"
              labelText="Apodo"
              control={control}
              placeholder="Ingresar"
              height="36px"
              maxLength={10}
              allowedPattern={onlyAlphanumericAndSpaces}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <InputField
              inputName="localidad"
              labelText="Localidad de residencia"
              control={control}
              placeholder="Ingresar"
              minHeight="36px"
              maxLength={15}
              allowedPattern={onlyLetters}
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px">
            <SelectComponent
              fullWidth
              minHeight="36px"
              items={[
                {
                  label: "Sí",
                  value: true,
                },
                {
                  label: "No",
                  value: false,
                },
              ]}
              name="agro"
              clearable
              closeOnSelect
              control={control}
              labelText={agroLabelText}
              placeholder="Seleccionar"
            />
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px" h="28px">
            <Field.Root>
              <Field.Label>
                {" "}
                <Text fontWeight={600} fontSize="16px">
                  Nivel de estudio
                </Text>
              </Field.Label>

              <Box width="100%">
                <AutocompleteSelect
                  disabled={loadingAuxEntities}
                  loading={loadingAuxEntities}
                  inputName="nivelEstudio"
                  items={
                    nivelesEstudio?.map((est) => ({
                      label: est.nombre,
                      value: est.id,
                    })) ?? []
                  }
                  control={control}
                />
              </Box>
            </Field.Root>
          </GridItem>
          <GridItem paddingX="16px" paddingY="8px" h="28px">
            <Field.Root>
              <Field.Label>
                {" "}
                <Text fontWeight={600} fontSize="16px">
                  Estado civil
                </Text>
              </Field.Label>

              <Box width="100%">
                <AutocompleteSelect
                  disabled={loadingAuxEntities}
                  loading={loadingAuxEntities}
                  inputName="estadoCivil"
                  items={
                    estadosCiviles?.map((civ) => ({
                      label: civ.nombre,
                      value: civ.id,
                    })) ?? []
                  }
                  control={control}
                />
              </Box>
            </Field.Root>
          </GridItem>

          <GridItem paddingX="16px" paddingY="8px">
            <Field.Root>
              <Field.Label>
                {" "}
                <Text fontWeight={600} fontSize="16px">
                  Profesion
                </Text>
              </Field.Label>

              <Box width="100%">
                <AutocompleteSelect
                  disabled={loadingAuxEntities}
                  loading={loadingAuxEntities}
                  inputName="profesion"
                  items={
                    profesiones?.map((pro) => ({
                      label: pro.nombre,
                      value: pro.id,
                    })) ?? []
                  }
                  control={control}
                />
              </Box>
            </Field.Root>
          </GridItem>
          <GridItem colSpan={4} paddingX="16px" paddingY="8px">
            <Field.Root>
              <Field.Label>
                {" "}
                <Text fontWeight={600} fontSize="16px">
                  Hobbies
                </Text>
              </Field.Label>
              <Box width="100%">
                <AutocompleteSelect
                  inputName="hobbies"
                  items={
                    hobbies?.map((hobbie) => ({
                      label: hobbie.nombre,
                      value: hobbie.id,
                    })) ?? []
                  }
                  control={control}
                  multiple
                  loading={loadingAuxEntities}
                  disabled={loadingAuxEntities}
                />
              </Box>
            </Field.Root>
          </GridItem>
        </Grid>
      </Card.Body>
    </Card.Root>
  );
};
